/*
  Pickup time selector — Faircrest Farms
  Save as: assets/pickup-time.js

  Loaded once per page. Watches for any .pickup-time markup appearing in the
  DOM and wires it up — so it works on the cart page, in the add-to-cart
  notification, and in the cart drawer, including after Dawn re-renders those
  sections with innerHTML.
*/

(function () {
  'use strict';

  // The snippet may render more than once on a page (cart footer + notification),
  // which means this file can be requested more than once. Only run it once.
  if (window.__pickupTimeLoaded) return;
  window.__pickupTimeLoaded = true;

  var TZ = 'America/Denver';
  var ATTR = 'Pickup Time';
  var ATTR_ISO = '_pickup_iso';
  var LEAD_MS = 2 * 60 * 60 * 1000;
  var IDLE_MS = 30 * 60 * 1000;
  var RECHECK_MS = 60 * 1000;

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // dow: 0 = Sunday. sh/sm = start hour/minute, Mountain Time.
  var SLOTS = [
    { dow: 3, sh: 16, sm: 0, window: '4–7pm', period: 'evening' },
    { dow: 4, sh: 8, sm: 0, window: '8–11am', period: 'morning' },
    { dow: 5, sh: 8, sm: 0, window: '8–11am', period: 'morning' },
    { dow: 6, sh: 8, sm: 0, window: '8–11am', period: 'morning' }
  ];

  /* ---- Shared state ------------------------------------------------- */
  /* One selection, many mount points. Picking a slot in the popup updates
     the cart page copy too, without either knowing about the other. */

  var roots = [];
  var selected = '';
  var saving = false;
  var idleTimer = null;
  var noticeText = '';

  /* ---- Clock -------------------------------------------------------- */
  /* Anchored to the server's time so a wrong device clock can't offer the
     wrong slots. Only elapsed time is read from the device. */

  var hasPerf = !!(window.performance && window.performance.now);
  var baseEpoch = 0;
  var perfStart = 0;
  var dateStart = 0;
  var clockSet = false;

  function setClock(seconds) {
    if (clockSet) return;
    var s = parseInt(seconds, 10);
    if (!(s > 0)) return;
    baseEpoch = s * 1000;
    perfStart = hasPerf ? performance.now() : 0;
    dateStart = Date.now();
    clockSet = true;
  }

  function nowMs() {
    if (!clockSet) return Date.now();
    var elapsed = hasPerf ? performance.now() - perfStart : Date.now() - dateStart;
    return baseEpoch + elapsed;
  }

  var dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short'
  });

  function partsIn(ms) {
    var p = {};
    dtf.formatToParts(new Date(ms)).forEach(function (part) {
      p[part.type] = part.value;
    });
    return {
      y: parseInt(p.year, 10),
      mo: parseInt(p.month, 10),
      d: parseInt(p.day, 10),
      h: p.hour === '24' ? 0 : parseInt(p.hour, 10),
      mi: parseInt(p.minute, 10),
      dow: SHORT.indexOf(p.weekday)
    };
  }

  // Mountain Time wall clock -> epoch. Handles DST with no hardcoded offset.
  function zonedToEpoch(y, mo, d, h, mi) {
    var guess = Date.UTC(y, mo - 1, d, h, mi);
    var p = partsIn(guess);
    var roundTrip = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
    return guess - (roundTrip - guess);
  }

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  /* ---- Slots -------------------------------------------------------- */

  function buildSlots() {
    var p = partsIn(nowMs());
    var todayUTC = Date.UTC(p.y, p.mo - 1, p.d);
    var weekStart = todayUTC - p.dow * 86400000; // Sunday of the current week

    return SLOTS.map(function (slot) {
      var day = new Date(weekStart + slot.dow * 86400000);
      var y = day.getUTCFullYear();
      var mo = day.getUTCMonth() + 1;
      var d = day.getUTCDate();
      var start = zonedToEpoch(y, mo, d, slot.sh, slot.sm);

      return {
        value: y + '-' + pad(mo) + '-' + pad(d) + 'T' + pad(slot.sh) + ':' + pad(slot.sm),
        dayLabel: DAYS[slot.dow] + ', ' + MONTHS[mo - 1] + ' ' + d,
        window: slot.window,
        fullLabel: DAYS[slot.dow] + ' ' + slot.period,
        cutoff: start - LEAD_MS
      };
    });
  }

  /* ---- Cart --------------------------------------------------------- */

  function save(value, label) {
    saving = true;
    toggleExpressButtons(false);

    var attributes = {};
    attributes[ATTR] = label || '';
    attributes[ATTR_ISO] = value || '';

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    })
      .catch(function () {
        showError('That did not save. Check your connection and pick a time again.');
      })
      .then(function () {
        saving = false;
        toggleExpressButtons(true);
      });
  }

  // Express buttons leave the page immediately; hold them until the
  // attribute has actually been written.
  function toggleExpressButtons(enable) {
    var wraps = document.querySelectorAll('.cart__dynamic-checkout-buttons');
    Array.prototype.forEach.call(wraps, function (wrap) {
      wrap.style.pointerEvents = enable ? '' : 'none';
      wrap.style.opacity = enable ? '' : '0.5';
    });
  }

  /* ---- Messages ----------------------------------------------------- */

  function showError(msg) {
    live().forEach(function (root) {
      var el = root.querySelector('[data-pickup-error]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
    });
  }

  function clearError() {
    live().forEach(function (root) {
      var el = root.querySelector('[data-pickup-error]');
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    });
  }

  function setNotice(msg) {
    noticeText = msg || '';
    live().forEach(function (root) {
      var el = root.querySelector('[data-pickup-notice]');
      if (!el) return;
      el.textContent = noticeText;
      el.hidden = !noticeText;
    });
  }

  /* ---- Mounting ----------------------------------------------------- */

  function live() {
    roots = roots.filter(function (r) {
      return r.isConnected;
    });
    return roots;
  }

  function mount(root) {
    if (root.dataset.pickupMounted === 'true') return;
    root.dataset.pickupMounted = 'true';

    setClock(root.dataset.now);

    // Server-rendered value wins on first mount, so a reload restores the pick.
    if (!selected && root.dataset.selected) selected = root.dataset.selected;

    var list = root.querySelector('[data-pickup-list]');
    if (list) {
      list.addEventListener('change', function (e) {
        var input = e.target;
        if (!input || input.name !== 'pickup-time') return;
        selected = input.value;
        clearError();
        setNotice('');
        save(selected, input.dataset.label);
        renderAll();
        resetIdle();
      });
    }

    roots.push(root);
    renderAll();
  }

  function scan() {
    var found = document.querySelectorAll('.pickup-time:not([data-pickup-mounted])');
    Array.prototype.forEach.call(found, mount);
  }

  /* ---- Render ------------------------------------------------------- */

  function renderOne(root, slots, now) {
    var list = root.querySelector('[data-pickup-list]');
    if (!list) return;

    var scope = root.dataset.pickupScope || 'cart';
    list.textContent = '';

    slots.forEach(function (slot, i) {
      var open = now < slot.cutoff;
      var id = 'pickup-' + scope + '-' + i;

      var label = document.createElement('label');
      label.className = 'pickup-time__option';
      label.setAttribute('for', id);
      if (!open) label.classList.add('pickup-time__option--closed');
      if (open && selected === slot.value) label.classList.add('pickup-time__option--selected');

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'pickup-time';
      input.id = id;
      input.className = 'pickup-time__input';
      input.value = slot.value;
      input.disabled = !open;
      input.checked = open && selected === slot.value;
      input.dataset.label = slot.fullLabel;

      var text = document.createElement('span');
      text.className = 'pickup-time__text';

      var day = document.createElement('span');
      day.className = 'pickup-time__day';
      day.textContent = slot.dayLabel;

      var win = document.createElement('span');
      win.className = 'pickup-time__window';
      win.textContent = open ? slot.window : slot.window + ' — closed';

      text.appendChild(day);
      text.appendChild(win);
      label.appendChild(input);
      label.appendChild(text);
      list.appendChild(label);
    });

    var notice = root.querySelector('[data-pickup-notice]');
    if (notice) {
      notice.textContent = noticeText;
      notice.hidden = !noticeText;
    }
  }

  function renderAll() {
    var slots = buildSlots();
    var now = nowMs();
    var anyOpen = slots.some(function (s) {
      return now < s.cutoff;
    });

    // A selection that has since closed is no longer valid.
    if (selected && !slots.some(function (s) { return s.value === selected && now < s.cutoff; })) {
      selected = '';
      save('', '');
    }

    if (!anyOpen) {
      noticeText = 'Ordering is open Wednesday through Friday for that week\u2019s pickups. Come back Wednesday to order from the new harvest.';
    } else if (noticeText.indexOf('Ordering is open') === 0) {
      noticeText = '';
    }

    live().forEach(function (root) {
      renderOne(root, slots, now);
    });

    return anyOpen;
  }

  /* ---- Idle --------------------------------------------------------- */

  function resetIdle() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(onIdle, IDLE_MS);
  }

  function onIdle() {
    if (!selected) return;
    selected = '';
    save('', '');
    renderAll();
    setNotice('Your pickup time was cleared after 30 minutes of inactivity. Choose it again before checking out.');
  }

  /* ---- Checkout interception ---------------------------------------- */
  /* Document-level and capture-phase, so it covers the cart page form, the
     notification form, and the drawer form without knowing their ids. */

  function validateOrBlock(e, root) {
    var slots = buildSlots();
    var now = nowMs();
    var valid = selected && slots.some(function (s) {
      return s.value === selected && now < s.cutoff;
    });

    if (valid && !saving) return true;

    e.preventDefault();
    e.stopPropagation();

    var anyOpen = renderAll();

    if (!anyOpen) {
      showError('No pickup times are available right now, so this order can\u2019t be placed.');
    } else if (saving) {
      showError('Still saving your pickup time. Try again in a moment.');
    } else if (selected) {
      showError('That pickup time just closed. Choose another.');
    } else {
      showError('Choose a pickup time to continue.');
    }

    if (root) root.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return false;
  }

  // Find the selector nearest the checkout button that was pressed, so the
  // error message appears in the popup the customer is actually looking at.
  function nearestRoot(el) {
    var container = el.closest('.cart-notification, .cart-drawer, .cart__footer, body');
    var found = container ? container.querySelector('.pickup-time') : null;
    return found || live()[0] || null;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[name="checkout"]') : null;
    if (!btn) return;
    if (!live().length) return;
    validateOrBlock(e, nearestRoot(btn));
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart') === -1) return;
    if (e.submitter && e.submitter.name && e.submitter.name !== 'checkout') return;
    if (!live().length) return;
    validateOrBlock(e, nearestRoot(form));
  }, true);

  /* ---- Boot --------------------------------------------------------- */

  ['click', 'keydown', 'scroll'].forEach(function (evt) {
    document.addEventListener(evt, resetIdle, { passive: true });
  });

  // Dawn swaps the notification and drawer contents with innerHTML, which
  // fires no event we can hook. Watching the DOM catches every case.
  var observer = new MutationObserver(function () {
    scan();
  });

  function boot() {
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
    setInterval(renderAll, RECHECK_MS);
    resetIdle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();